// Accounting's long lists (Invoices, Payments and refunds): the filter as a page keeps it (a search that
// waits for the typing to stop), the pages after the first read with "Show more", and the region's state
// from what's read. Kept apart from the pages so each page's module draws only its own parts.
import { useEffect, useState } from 'react';
import type { ListStatus } from '@flowology/ui-web/patterns';
import type { MoreRows } from '@flowology/ui-web/fl';
import { client } from '../api.ts';
import { PAGE, type ListFilter } from './money-lists-model.ts';
import { errorText } from './ui.tsx';

/** A list page's filter: its choices, a search that waits for the typing to stop, and a range of days. */
export function useListFilter<S extends string>(): [
  ListFilter<S>,
  (f: Partial<ListFilter<S>>) => void,
  string,
  (s: string) => void,
] {
  const [f, setF] = useState<ListFilter<S>>({ show: 'all' as S, search: '', from: '', to: '' });
  const [typed, setTyped] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setF((o) => (o.search === typed ? o : { ...o, search: typed })), 300);
    return () => clearTimeout(t);
  }, [typed]);
  return [f, (p) => setF((o) => ({ ...o, ...p })), typed, setTyped];
}

/** A list's pages after the first, read with "Show more"; a new filter starts again from the first. */
export function useMore<R>(
  action: string,
  first: { rows: R[]; next: string | null } | null,
  input: (after: string) => unknown,
) {
  const [more, setMore] = useState<R[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setMore([]);
    setNext(first?.next ?? null);
    setError(null);
  }, [first]);
  const read = async () => {
    if (!next) return;
    setBusy(true);
    try {
      const p = await client.call<{ rows: R[]; next: string | null }>(action, input(next));
      setMore((m) => [...m, ...p.rows]);
      setNext(p.next);
      setError(null);
    } catch (e) {
      setError(errorText(e, 'Could not read more. Try again.'));
    } finally {
      setBusy(false);
    }
  };
  return { rows: [...(first?.rows ?? []), ...more], next, busy, error, read };
}

/** "Show 50 more of 120" under the rows while there's a next page; null when there's none. */
export function moreRows(
  m: { next: string | null; busy: boolean; error: string | null; read: () => Promise<void>; rows: unknown[] },
  total: number,
): MoreRows | null {
  if (!m.next) return null;
  return {
    label: `Show ${Math.min(PAGE, total - m.rows.length)} more of ${total}`,
    onPress: () => void m.read(),
    isBusy: m.busy,
    error: m.error,
  };
}

/** A region's state from what's read: an answer (ready; its rows say their own empty), or the refusal. */
export const listStatus = (data: unknown, error: string | null): ListStatus =>
  data ? 'ready' : error ? 'failed' : 'loading';
